import 'maplibre-gl/dist/maplibre-gl.css';

import type { LngLatLike, Map } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_PITCH = 0;
const DEFAULT_BEARING = 0;

interface MapLibreContextValue {
  map: Map | null;
}

const MapLibreContext = createContext<MapLibreContextValue>({ map: null });

interface MapLibreProviderProps {
  center: [number, number];
  zoom: number;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  jwt?: string;
  allowPitch?: boolean; // Whether to allow pitch/tilt controls
  initialPitch?: number;
  initialBearing?: number;
}

export const MapLibreProvider: React.FC<MapLibreProviderProps> = ({
  center,
  zoom,
  children,
  className,
  style,
  jwt,
  allowPitch = true,
  initialPitch,
  initialBearing,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const [isReady, setIsReady] = useState(false);
  const jwtRef = useRef<string | undefined>(jwt);

  useEffect(() => {
    jwtRef.current = jwt;
  }, [jwt]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) {
      return;
    }

    try {
      const initialCenter: LngLatLike = [center[1], center[0]]; // [lng, lat]
      mapRef.current = new maplibregl.Map({
        container: containerRef.current,
        center: initialCenter,
        zoom,
        pitch: typeof initialPitch === 'number' ? initialPitch : DEFAULT_PITCH,
        bearing: typeof initialBearing === 'number' ? initialBearing : DEFAULT_BEARING,
        pitchWithRotate: allowPitch, // Disable pitch control if not allowed
        dragRotate: allowPitch, // Disable rotation if pitch not allowed
        touchPitch: allowPitch, // Disable touch pitch if not allowed
        style: {
          version: 8,
          sources: {},
          layers: [],
          glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
        },
        attributionControl: false,
        antialias: true,
        transformRequest: (url) => {
          try {
            const currentJwt = jwtRef.current;
            if (currentJwt && (url.startsWith('/tiles') || url.includes('/tiles/'))) {
              return { url, headers: { Authorization: `Bearer ${currentJwt}` } } as maplibregl.RequestParameters;
            }
          } catch (error) {
            console.error(error);
            // noop
          }
          return { url } as maplibregl.RequestParameters;
        },
      });

      mapRef.current.once('load', () => {
        setIsReady(true);
      });

      mapRef.current.on('error', (e) => {
        console.error('MapLibre error:', e.error);
      });

    } catch (error) {
      console.error('Error initializing MapLibre:', error);
    }

    return () => {
      try {
        mapRef.current?.remove();
      } catch (error) {
        console.warn('Error removing map:', error);
      }
      mapRef.current = null;
      setIsReady(false);
    };
  }, []);

  // Disable automatic recentering after initialization to allow free navigation
  // The initial center and zoom are set during map creation, and after that
  // the parent component (CoproMap) handles all navigation logic

  const value = useMemo(() => ({ map: mapRef.current }), [isReady]);

  return (
    <MapLibreContext.Provider value={value}>
      <div ref={containerRef} className={className} style={style} />
      {isReady && children}
    </MapLibreContext.Provider>
  );
};

export function useMapLibre(): Map {
  const ctx = useContext(MapLibreContext);
  if (!ctx.map) {
    throw new Error('MapLibre map is not available yet');
  }
  return ctx.map;
}

export function useOptionalMapLibre(): Map | null {
  const ctx = useContext(MapLibreContext);
  return ctx.map;
}
